import * as React from "react";
import { formatMessage } from "lib/globalize-util";
import { $, media, merge } from "glamor";
import { Rules } from "./rules";
import Button from "components/form/button";
import TextInline from "components/form/text-inline";
import { theme } from "styles/theme";
import IconButton from "material-ui/IconButton";
import Trash from "components/icons/trash";
import { AddIcon } from "components/icons/add";
import { DATATEST } from "constants/integration";
import { AddHintBadge } from "components/hint-badge";
import type { IScope } from "reducers/scope";
import CustomToggle from "components/form/switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";

const titleCase = (name: string) => {
    if (!name || name.length === 0) {
        return name;
    }
    return name
        .trim()
        .split(" ")
        .map((word) => {
            return word !== "" ? word.replace(word[0], word[0].toUpperCase()) : word;
        })
        .join(" ");
};

export interface IProps {
    scope: IScope;
    canEditGroupName: boolean;
    fields: any;
    filters?: any;
    filterValues?: any;
    group: any;
    onAddRule: (event) => void;
    onAddValue: (event) => void;
    onRemoveRule: (event) => void;
    onRemoveValue: (groupId, ruleId, value) => void;
    onSelectField: (event) => void;
    onChangeGroupName?: (groupId, name) => void;
    onFetchFilterValues?: (action, field, value, options?) => void;
    onResetFilterValues?: () => void;
    onRemoveGroup?: (event) => void;
    onToggleGroupType?: (event) => void;
    onToggleRuleType?: (event) => void;
    onChangeExclude?: (groupId: string, exclude: boolean) => void;
}

export class RuleGroup extends React.PureComponent<IProps, any> {
    styles = merge(
        {
            border: `1px solid ${theme.palette.borderColor}`,
            borderRadius: "4px",
            display: "flex",
            flexDirection: "column",
            marginBottom: "24px",
        },
        $("> div", {
            alignItems: "center",
            display: "flex",
            justifyContent: "space-between",
        }),
        $("> div > div:first-child", {
            flexBasis: "50%",
        }),
        $(" form", {
            display: "flex",
            flexDirection: "column",
        }),
        $(" .toggleLabel", {
            fontSize: "14px",
            fontFamily: "inherit",
            color: `${theme.palette.textColor}`,
        }),
        $(" .toggleRoot", {
            margin: "5px 0",
        }),
        $(" .requiredFieldError", {
            color: `${theme.palette.warning} !important`,
        }),
        $(" .requiredFieldError:after", {
            content: "*",
        }),
        $(" .groupActions", {
            background: "#f7f7f7",
            padding: "24px 16px",
        }),
        $(" .addRuleIconButton", {
            display: "none !important",
        }),
        $(" .removeGroupIconButton", {
            display: "none !important",
        }),
        $(" .public-DraftEditorPlaceholder-root", {
            color: "unset",
        }),
        $(" .excludeGroupContainer", {
            padding: "0 10px",
        }),
        media(
            "(max-width: 600px)",
            $(" .addRuleButton", {
                display: "none",
            }),
            $(" .addRuleIconButton", {
                display: "inline-block !important",
            }),
            $(" .removeGroupButton", {
                display: "none",
            }),
            $(" .removeGroupIconButton", {
                display: "inline-block !important",
            })
        )
    );

    draftJSStyles: any = {
        textInline: {
            margin: 0,
        },
    };

    render() {
        const {
            scope,
            canEditGroupName,
            fields,
            filters,
            filterValues,
            group,
            onAddRule,
            onAddValue,
            onFetchFilterValues,
            onRemoveGroup,
            onRemoveRule,
            onRemoveValue,
            onResetFilterValues,
            onSelectField,
            onToggleGroupType,
            onToggleRuleType,
            onChangeExclude,
        } = this.props;
        const { _id, name, product_filter_values: rules, type, exclude = false } = group.toObject();
        const useCatalog = scope.brand.hasPreference("linesheets_are_catalogs");
        return (
            <div data-test={DATATEST.CUSTOMER_USER_GROUPS.CUSTOMER_GROUP} className="group" {...this.styles}>
                <div className="groupActions">
                    <TextInline
                        dataTest={DATATEST.CUSTOMER_USER_GROUPS.GROUP_NAME_INPUT}
                        value={titleCase(name) || ""}
                        defaultFocus={_id.includes("_")}
                        editable={canEditGroupName}
                        key={_id}
                        maxLength={46}
                        multiLine={false}
                        onChange={this.handleChange}
                        placeholder={formatMessage("Untitled Group")}
                        style={this.draftJSStyles.textInline}
                        updateTimeout={0}
                    />
                    {onToggleGroupType && (
                        <FormControlLabel
                            classes={{ root: "toggleRoot", label: "toggleLabel" }}
                            labelPlacement="start"
                            control={
                                <CustomToggle
                                    inputProps={
                                        {
                                            value: type,
                                            "data-test": DATATEST.CUSTOMER_USER_GROUPS.WHITELIST_TOGGLE,
                                        } as any
                                    }
                                    checked={type === "whitelist"}
                                    onChange={this.handleChangeToggle}
                                />
                            }
                            label={formatMessage("Whitelist")}
                        />
                    )}
                    <div className="addRuleButton">
                        <Button
                            data-addrule={_id}
                            disabled={rules.size === fields.size}
                            label={formatMessage("Add Rule")}
                            data-test={DATATEST.CUSTOMER_USER_GROUPS.ADD_RULE_BTN}
                            onClick={onAddRule}
                        />
                    </div>
                    <IconButton
                        data-addrule={_id}
                        className="addRuleIconButton"
                        disabled={rules.size === fields.size}
                        onClick={onAddRule}
                    >
                        <AddIcon />
                    </IconButton>
                    {onRemoveGroup && (
                        <div className="removeGroupButton">
                            <Button
                                data-removegroup={_id}
                                label={formatMessage("Remove Group")}
                                data-test={DATATEST.CUSTOMER_USER_GROUPS.REMOVE_GROUP_BTN}
                                onClick={onRemoveGroup}
                            />
                        </div>
                    )}
                    {onRemoveGroup && (
                        <IconButton className="removeGroupIconButton" data-removegroup={_id} onClick={onRemoveGroup}>
                            <Trash />
                        </IconButton>
                    )}
                </div>
                {onChangeExclude && (
                    <div className="excludeContainer excludeGroupContainer">
                        <AddHintBadge
                            hint="Enable to exclude any user in the customer group from all linesheets."
                            variables={{
                                linesheets: useCatalog ? formatMessage("catalogs") : formatMessage("linesheets"),
                            }}
                        >
                            <FormControlLabel
                                classes={{ root: "toggleRoot", label: "toggleLabel" }}
                                control={
                                    <CustomToggle
                                        inputProps={
                                            { "data-test": DATATEST.CUSTOMER_USER_GROUPS.EXCLUDE_GROUP_TOGGLE } as any
                                        }
                                        checked={exclude}
                                        onChange={this.handleChangeExclude}
                                    />
                                }
                                label={formatMessage("Exclude this group from all Linesheets", {
                                    Linesheets: useCatalog ? formatMessage("Catalogs") : formatMessage("Linesheets"),
                                })}
                            />
                        </AddHintBadge>
                    </div>
                )}
                <Rules
                    filters={filters}
                    filterValues={filterValues}
                    groupId={_id}
                    onAddValue={onAddValue}
                    onFetchFilterValues={onFetchFilterValues}
                    onRemoveRule={onRemoveRule}
                    onRemoveValue={onRemoveValue}
                    onResetFilterValues={onResetFilterValues}
                    onSelectField={onSelectField}
                    onToggleRuleType={onToggleRuleType}
                    rules={rules}
                    topLevelFields={fields}
                />
            </div>
        );
    }

    private handleChange = (name) => {
        const { group, onChangeGroupName } = this.props;
        onChangeGroupName(group.get("_id"), name === "" ? formatMessage("Untitled Group") : name);
    };

    private handleChangeExclude = () => {
        const { group, onChangeExclude } = this.props;
        const { exclude = false } = group.toObject();
        onChangeExclude(group.get("_id"), !exclude);
    };

    private handleChangeToggle = () => {
        const { group, onToggleGroupType } = this.props;
        onToggleGroupType(group.get("_id"));
    };
}

export default RuleGroup;
